import { HttpError } from 'core/utils'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { EditJournalForm, EditJournalFormData } from './EditJournalForm'
import { useJournalsApi } from 'core/contexts/JournalsApiContext/JournalsApiContext'
import { useNotifications } from 'shared/Notifications'
import { useNavigate } from 'react-router-dom'
import { ModalController } from 'shared/Modal'
import { addJournal, useJournals } from 'core/api/journals'

type AddJournalDialogProps = {
  controller: ModalController<boolean>
}
export function AddJournalDialog({ controller }: Readonly<AddJournalDialogProps>) {
  const { updateJournals } = useJournalsApi()
  const journals = useJournals()
  const journalsRef = useRef(journals)
  useEffect(() => {
    journalsRef.current = journals
  }, [journals])

  const [loading, setLoading] = useState(false)
  const [occupiedName, setOccupiedName] = useState<string | null>(null)

  const useFormReturn = useForm<EditJournalFormData>()
  const {
    handleSubmit,
    setValue,
    clearErrors,
    trigger,
    formState: { isValid },
  } = useFormReturn

  const { showNotification } = useNotifications()
  const navigate = useNavigate()

  const close = () => {
    clearErrors()
    setValue('journalName', '')
    setValue('journalType', '')
    setOccupiedName(null)
    controller.close()
  }

  const onSubmit = handleSubmit(async (data) => {
    if (!isValid) return
    setLoading(true)

    try {
      const response = await addJournal(data.journalName, data.journalType)
      const { journals: updatedJournals } = await updateJournals()
      if (!updatedJournals) {
        console.error('Failed to update journals')
        return
      }

      showNotification({
        text: `Journal "${response.journalName}" added`,
        type: 'success',
        action: () => {
          /* TODO: review this part, move to argument callback*/
          const newJournal = updatedJournals.find((j) => j.id === response.id)
          if (!newJournal) return
          navigate('/home/dashboard?journal_id=' + newJournal.id, { replace: true })
          if (window.location.pathname === '/home/dashboard') window.location.reload()
        },
        actionName: 'Open',
      })
      close()
    } catch (error) {
      console.error('Failed to add journal\n', (error as any).body)
      if (error instanceof HttpError) {
        if (error.status === 400) {
          setOccupiedName(data.journalName)
        }
      }
      setTimeout(() => trigger())
    } finally {
      setLoading(false)
    }
  })

  return (
    <EditJournalForm
      loading={loading}
      occupiedName={occupiedName}
      useFormReturn={useFormReturn}
      onSubmit={onSubmit}
      onCancel={() => close()}
      actionText="Add"
    />
  )
}
